import { Component, ChangeDetectionStrategy, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CdkDrag, CdkDragDrop, CdkDragPreview, CdkDropList, DragDropModule } from '@angular/cdk/drag-drop';
import { TemplateTransferService } from '../../services/template-transfer.service';
import { TemplateAssignmentService } from '../../services/template-assignment.service';
import { DocumentTemplateService } from '../../services/document-template.service';
import { OcrPrimeStore } from '../../store/ocr-prime.store';
import { DocumentTemplateWithPath } from '../../types/document-template';
import {
  AssignmentRequest,
  TemplateTreeViewComponent,
  UNPLACED_DROP_LIST_ID
} from './components/template-tree-view/template-tree-view.component';
import { TemplateCardComponent } from './components/template-card/template-card.component';
import { TemplateDragPreviewComponent } from './components/template-drag-preview/template-drag-preview.component';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../components/confirm-dialog/confirm-dialog.component';
import { collectLeaves } from '../../utils/anchor-tree.utils';

/** Set on <body> while a card is in flight (see styles.scss). */
const DRAGGING_BODY_CLASS = 'template-drag-active';

/** Duration of the side-panel collapse/expand transition (keep in sync with `.unplaced-panel`). */
const PANEL_TRANSITION_MS = 200;

@Component({
  selector: 'app-templates',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule, RouterModule, MatIconModule, MatTooltipModule,
    DragDropModule, CdkDropList, CdkDrag, CdkDragPreview,
    TemplateTreeViewComponent, TemplateCardComponent, TemplateDragPreviewComponent
  ],
  styles: [`
    :host {
      display: block;
      height: 100%;
    }
    .unplaced-panel {
      width: 27rem;
      transition: width 200ms ease-out, border-color 150ms ease-out, background-color 150ms ease-out;
    }
    .unplaced-panel.is-collapsed {
      width: 3.5rem;
    }
    /* Every panel state can receive the card in flight… */
    .unplaced-panel.is-drop-candidate {
      border-style: dashed;
      border-color: rgb(165 180 252 / 0.9);
    }
    /* …dropping here unassigns the template, so the whole panel lights up —
       the rail is too narrow to carry an inner highlight. */
    .unplaced-panel.is-drop-target {
      border-color: #6366f1;
      box-shadow: inset 0 0 0 1px #6366f1;
      background-color: rgb(99 102 241 / 0.06);
    }
    .rail-label {
      writing-mode: vertical-rl;
      transform: rotate(180deg);
      letter-spacing: 0.08em;
    }
    /* Keep the source card visible but muted so its origin stays obvious. */
    :host ::ng-deep .unplaced-drop-list .cdk-drag-placeholder { opacity: 0.35; }
    /* A card-sized placeholder cannot fit the rail, which signals the drop
       through the panel highlight instead. */
    :host ::ng-deep .unplaced-panel.is-collapsed .cdk-drag-placeholder { display: none; }
    :host ::ng-deep .unplaced-drop-list .cdk-drag { cursor: grab; }
    :host ::ng-deep .unplaced-drop-list .cdk-drag:active { cursor: grabbing; }
    /* The card is also a stretched navigation link; keep the drag affordance. */
    :host ::ng-deep .unplaced-drop-list .cdk-drag a[aria-hidden='true'] { cursor: inherit; }
    @media (prefers-reduced-motion: reduce) {
      .unplaced-panel { transition: none; }
    }
  `],
  template: `
    <div class="h-full flex flex-col p-6 overflow-hidden">
      <header class="flex items-center justify-between mb-6 flex-none">
        <div>
          <h1 class="text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">Template Management</h1>
          <p class="text-zinc-500 dark:text-zinc-400 mt-1">
            Drag templates onto anchor-tree leaves to make them matchable.
          </p>
        </div>
        <div class="flex items-center gap-3">
          <a
            routerLink="/anchor-tree"
            data-testid="templates-edit-anchor-tree-link"
            matTooltip="Edit the anchor tree structure (decisions, branches, leaves)"
            class="inline-flex items-center gap-2 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-4 py-2.5 text-sm font-medium text-zinc-700 dark:text-zinc-200 shadow-sm hover:bg-zinc-50 dark:hover:bg-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-zinc-800 cursor-pointer transition-colors"
          >
            <mat-icon class="!text-base !w-4 !h-4 !leading-4">account_tree</mat-icon>
            Edit Anchor Tree
          </a>
          <button
            (click)="importTemplate()"
            [disabled]="isImporting()"
            matTooltip="Imports a template that was previously exported to your clipboard"
            class="inline-flex items-center gap-2 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-4 py-2.5 text-sm font-medium text-zinc-700 dark:text-zinc-200 shadow-sm hover:bg-zinc-50 dark:hover:bg-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-zinc-800 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 0l3 3m-3-3l3-3M3 12a9 9 0 1118 0 9 9 0 01-18 0z"/>
            </svg>
            {{ isImporting() ? 'Importing...' : 'Import Template' }}
          </button>
          <button
            routerLink="/add-template"
            class="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer transition-colors"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            Create Template
          </button>
        </div>
      </header>

      <main class="flex-1 overflow-hidden flex gap-4 min-h-0">
        <!-- Tree canvas with template cards at leaves -->
        <section class="flex-1 h-full overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50/30 dark:bg-zinc-800/30 p-4">
          <app-template-tree-view
            [assigningTemplateId]="assigningTemplateId()"
            [deletingTemplateId]="deletingTemplateId()"
            [togglingTemplateId]="togglingTemplateId()"
            [dragActive]="isDraggingCard()"
            (cardDragStarted)="isDraggingCard.set(true)"
            (cardDragEnded)="onCardDragEnded()"
            (assignmentRequested)="onAssignmentRequested($event)"
            (deleteClicked)="deleteTemplate($event)"
            (exportClicked)="exportTemplate($event)"
            (toggleBlockedClicked)="toggleBlocked($event)"
            (reextractClicked)="reextractAnchors($event)"
          />
        </section>

        <!-- Unplaced templates side list -->
        <aside
          class="unplaced-panel flex-none h-full flex flex-col overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800"
          [class.is-collapsed]="isPanelCollapsed()"
          [class.is-drop-candidate]="isDraggingCard() && !isUnplacedDropTarget()"
          [class.is-drop-target]="isUnplacedDropTarget()"
          [attr.data-collapsed]="isPanelCollapsed()"
          data-testid="unplaced-templates-panel"
        >
          @if (isPanelCollapsed()) {
            <button
              type="button"
              (click)="togglePanel()"
              matTooltip="Show unplaced templates"
              matTooltipPosition="left"
              [matTooltipShowDelay]="350"
              [matTooltipDisabled]="isPanelResizing()"
              aria-label="Show unplaced templates"
              [attr.aria-controls]="unplacedDropListId"
              [attr.aria-expanded]="false"
              data-testid="unplaced-panel-toggle"
              class="flex-none flex h-11 w-full items-center justify-center text-zinc-500 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-700/60 hover:text-zinc-700 dark:hover:text-zinc-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 cursor-pointer transition-colors"
            >
              <mat-icon class="!text-lg !w-5 !h-5 !leading-5">chevron_left</mat-icon>
            </button>
          } @else {
            <div class="flex-none px-4 pt-4">
              <div class="flex items-center gap-2 mb-1">
                <mat-icon class="!text-base !w-4 !h-4 !leading-4 text-zinc-400">location_off</mat-icon>
                <h2 class="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Unplaced templates</h2>
                <span class="text-xs text-zinc-400 tabular-nums">({{ unplacedTemplates().length }})</span>
                <button
                  type="button"
                  (click)="togglePanel()"
                  matTooltip="Collapse panel"
                  matTooltipPosition="left"
                  [matTooltipShowDelay]="350"
                  [matTooltipDisabled]="isPanelResizing()"
                  aria-label="Hide unplaced templates"
                  [attr.aria-controls]="unplacedDropListId"
                  [attr.aria-expanded]="true"
                  data-testid="unplaced-panel-toggle"
                  class="ml-auto -mr-1.5 flex h-9 w-9 flex-none items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-700 hover:text-zinc-600 dark:hover:text-zinc-200 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer transition-colors"
                >
                  <mat-icon class="!text-lg !w-5 !h-5 !leading-5">chevron_right</mat-icon>
                </button>
              </div>
              <p class="text-xs dark:text-zinc-400 mb-3">
                Unreachable — drag onto a leaf to assign.
              </p>
            </div>
          }
          <div
            class="unplaced-drop-list flex-1 min-h-0 rounded-lg"
            [class]="isPanelCollapsed()
              ? 'flex flex-col items-center gap-2 pb-3 cursor-pointer'
              : 'space-y-3 overflow-y-auto thin-scroll px-4 pb-4'"
            (click)="expandPanel()"
            cdkDropList
            [id]="unplacedDropListId"
            [cdkDropListData]="unplacedDropListId"
            [cdkDropListConnectedTo]="leafDropListIds()"
            [cdkDropListSortingDisabled]="true"
            (cdkDropListEntered)="isUnplacedDropTarget.set(true)"
            (cdkDropListExited)="isUnplacedDropTarget.set(false)"
            (cdkDropListDropped)="onDroppedOnUnplaced($event)"
          >
            @if (isPanelCollapsed()) {
              <mat-icon class="!text-lg !w-5 !h-5 !leading-5 flex-none text-zinc-500 dark:text-zinc-400" aria-hidden="true">location_off</mat-icon>
              @if (unplacedTemplates().length > 0) {
                <span
                  class="flex-none rounded-full bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-amber-700 dark:text-amber-300"
                  data-testid="unplaced-panel-badge"
                >{{ unplacedTemplates().length }}</span>
              }
              <span class="rail-label mt-1 max-h-full overflow-hidden text-[11px] font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                {{ isDraggingCard() ? 'Drop to unassign' : 'Unplaced' }}
              </span>
            } @else {
              @for (template of unplacedTemplates(); track template.id) {
                <div
                  cdkDrag
                  [cdkDragData]="template"
                  (cdkDragStarted)="isDraggingCard.set(true)"
                  (cdkDragEnded)="onCardDragEnded()"
                >
                  <ng-template cdkDragPreview>
                    <app-template-drag-preview [template]="template" hint="Drop on a leaf to assign" />
                  </ng-template>
                  <app-template-card
                    [template]="template"
                    [structure]="store.structuresMap()[template.fieldsStructureId]"
                    anchorStatus="unplaced"
                    [isDeleting]="deletingTemplateId() === template.id"
                    [isBusy]="assigningTemplateId() === template.id || togglingTemplateId() === template.id"
                    (deleteClicked)="deleteTemplate($event)"
                    (exportClicked)="exportTemplate($event)"
                    (toggleBlockedClicked)="toggleBlocked($event)"
                  />
                </div>
              } @empty {
                <div class="text-xs text-zinc-400 dark:text-zinc-500 text-center py-6" data-testid="unplaced-empty">
                  All templates are placed on the tree.
                </div>
              }
            }
          </div>
        </aside>
      </main>
    </div>
  `
})
export class TemplatesComponent {
  readonly store = inject(OcrPrimeStore);
  private readonly templateTransferService = inject(TemplateTransferService);
  private readonly assignmentService = inject(TemplateAssignmentService);
  private readonly documentTemplateService = inject(DocumentTemplateService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly document = inject(DOCUMENT);

  readonly unplacedDropListId = UNPLACED_DROP_LIST_ID;
  readonly isImporting = signal(false);
  readonly assigningTemplateId = signal<string | null>(null);
  readonly deletingTemplateId = signal<string | null>(null);
  readonly togglingTemplateId = signal<string | null>(null);
  /** True while a card is in flight, from either the tree or the side list. */
  readonly isDraggingCard = signal(false);
  /** True while the dragged card hovers the unplaced list (drop = unassign). */
  readonly isUnplacedDropTarget = signal(false);
  /**
   * Side panel collapsed into a narrow rail. Starts collapsed when nothing is
   * unplaced (the tree gets the full width) and open otherwise; from the first
   * toggle on, the author's choice wins.
   */
  readonly isPanelCollapsed = signal(false);
  /**
   * The panel width is mid-transition. Tooltips anchor to the position their
   * trigger had when the overlay was created, so a tooltip opened while the
   * toggle is still sliding would end up floating over the canvas.
   */
  protected readonly isPanelResizing = signal(false);
  private panelDefaultApplied = false;
  private panelResizeTimeout?: ReturnType<typeof setTimeout>;

  readonly unplacedTemplates = computed<DocumentTemplateWithPath[]>(() => {
    const unplaced = new Set(this.store.unplacedTemplateIds());
    return this.store.templates().filter(t => unplaced.has(t.id));
  });

  readonly leafDropListIds = computed<string[]>(() =>
    collectLeaves(this.store.anchorTreeRoot()).map(l => l.id)
  );

  constructor() {
    effect(onCleanup => {
      if (!this.isDraggingCard()) {
        return;
      }
      const body = this.document.body;
      body.classList.add(DRAGGING_BODY_CLASS);
      onCleanup(() => body.classList.remove(DRAGGING_BODY_CLASS));
    });

    inject(DestroyRef).onDestroy(() => clearTimeout(this.panelResizeTimeout));

    effect(() => {
      const loaded = this.store.templatesLoaded() && this.store.anchorTreeLoaded();
      if (this.panelDefaultApplied || !loaded) {
        return;
      }
      this.panelDefaultApplied = true;
      this.isPanelCollapsed.set(this.unplacedTemplates().length === 0);
    });
  }

  togglePanel(): void {
    this.panelDefaultApplied = true;
    this.isPanelCollapsed.update(collapsed => !collapsed);
    this.isPanelResizing.set(true);
    clearTimeout(this.panelResizeTimeout);
    this.panelResizeTimeout = setTimeout(() => this.isPanelResizing.set(false), PANEL_TRANSITION_MS);
  }

  expandPanel(): void {
    if (this.isPanelCollapsed()) {
      this.togglePanel();
    }
  }

  onCardDragEnded(): void {
    this.isDraggingCard.set(false);
    this.isUnplacedDropTarget.set(false);
  }

  async onAssignmentRequested(request: AssignmentRequest): Promise<void> {
    this.assigningTemplateId.set(request.template.id);
    try {
      await this.assignmentService.assignTemplateToLeaf(request.template, request.targetLeafNodeId);
    } finally {
      this.assigningTemplateId.set(null);
    }
  }

  async reextractAnchors(template: DocumentTemplateWithPath): Promise<void> {
    this.assigningTemplateId.set(template.id);
    try {
      await this.assignmentService.reextractForCurrentLeaf(template);
    } finally {
      this.assigningTemplateId.set(null);
    }
  }

  onDroppedOnUnplaced(event: CdkDragDrop<string>): void {
    if (event.previousContainer === event.container) {
      return;
    }
    const template = event.item.data as DocumentTemplateWithPath;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '440px',
      panelClass: 'field-dialog-panel',
      data: {
        title: `Unassign "${template.name}"?`,
        message: 'The template will be removed from its leaf and become unreachable for matching.',
        consequences: ['Its stored anchor values will be cleared (they are leaf-scoped).'],
        confirmLabel: 'Unassign',
      } as ConfirmDialogData
    });
    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) return;
      this.assigningTemplateId.set(template.id);
      try {
        await this.assignmentService.unassignTemplate(template);
        this.expandPanel();
      } finally {
        this.assigningTemplateId.set(null);
      }
    });
  }

  importTemplate(): void {
    this.isImporting.set(true);
    this.templateTransferService.importTemplate().subscribe({
      next: () => {
        this.snackBar.open('Template imported successfully!', 'Close', { duration: 3000 });
        this.store.refreshTemplates();
        this.isImporting.set(false);
      },
      error: (err) => {
        console.error('Error importing template:', err);
        this.snackBar.open(err.message || 'Failed to import template.', 'Close', { duration: 4000 });
        this.isImporting.set(false);
      }
    });
  }

  deleteTemplate(template: DocumentTemplateWithPath): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '440px',
      panelClass: 'field-dialog-panel',
      data: {
        title: `Delete template "${template.name}"?`,
        message: 'This action cannot be undone.',
        consequences: ['Documents matched to this template will no longer be processed with it.'],
        confirmLabel: 'Delete Template'
      } as ConfirmDialogData
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.deletingTemplateId.set(template.id);
      this.documentTemplateService.deleteDocumentTemplate(template.id).subscribe({
        next: () => {
          this.deletingTemplateId.set(null);
          this.store.refreshTemplates();
          // The backend also strips the template from its anchor-tree leaf.
          this.store.refreshAnchorTree();
          this.snackBar.open(`Template "${template.name}" deleted`, 'Close', { duration: 3000 });
        },
        error: (err) => {
          console.error('Error deleting document template:', err);
          this.deletingTemplateId.set(null);
          this.snackBar.open('Failed to delete the template. Please try again later.', 'Close', { duration: 4000 });
        }
      });
    });
  }

  toggleBlocked(template: DocumentTemplateWithPath): void {
    const newAutoBlocked = !template.autoBlocked;
    this.togglingTemplateId.set(template.id);
    this.documentTemplateService.updateDocumentTemplate(template.id, {
      name: template.name,
      description: template.description,
      structure: template.structure,
      fieldsStructureId: template.fieldsStructureId,
      autoBlocked: newAutoBlocked,
    }).subscribe({
      next: () => {
        this.togglingTemplateId.set(null);
        this.store.refreshTemplates();
        this.snackBar.open(
          newAutoBlocked
            ? `Template "${template.name}" blocked — associated documents will go to manual review`
            : `Template "${template.name}" unblocked`,
          'Close',
          { duration: 4000 }
        );
      },
      error: (err) => {
        console.error('Error toggling template blocked state:', err);
        this.togglingTemplateId.set(null);
        this.snackBar.open('Failed to update the template blocked state. Please try again later.', 'Close', { duration: 4000 });
      }
    });
  }

  async exportTemplate(template: DocumentTemplateWithPath): Promise<void> {
    try {
      await this.templateTransferService.exportTemplate(template);
      this.snackBar.open(`Template "${template.name}" copied to clipboard`, 'Close', { duration: 3000 });
    } catch (err) {
      console.error('Error exporting template:', err);
      this.snackBar.open('Failed to copy template to clipboard.', 'Close', { duration: 4000 });
    }
  }
}
